import React, { useState, useEffect } from 'react';
import { db } from '../../../utils/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Save, Loader } from 'lucide-react';
import ImageUploader from '../common/ImageUploader';

interface BrandSectionConfig {
  backgroundImage: string;
  overlayOpacity: number;
  headline: string;
  description: string;
  ctaText: string;
  ctaLink: string;
}

interface BrandSectionSettings {
  '4HVNSK8': BrandSectionConfig;
}

const DEFAULT_SETTINGS: BrandSectionSettings = {
  '4HVNSK8': {
    backgroundImage: '',
    overlayOpacity: 90,
    headline: 'PREMIUM\nCOLLECTION',
    description: 'Limited edition decks and essential apparel for serious skateboarders. Crafted with precision, designed for performance.',
    ctaText: 'EXPLORE 4HVNSK8',
    ctaLink: '/shop',
  },
};

const BrandSectionsManager: React.FC = () => {
  const [settings, setSettings] = useState<BrandSectionSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const docRef = doc(db, 'content', 'home');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists() && docSnap.data().brandSections) {
        const data = docSnap.data().brandSections;
        setSettings({
          '4HVNSK8': { ...DEFAULT_SETTINGS['4HVNSK8'], ...(data['4HVNSK8'] || {}) },
        });
      }
    } catch (error) {
      console.error('Error fetching brand section settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await setDoc(
        doc(db, 'content', 'home'),
        {
          brandSections: settings,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );
      setMessage({ type: 'success', text: 'Brand section settings saved successfully' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (
    brand: '4HVNSK8',
    field: keyof BrandSectionConfig,
    value: string | number
  ) => {
    setSettings((prev) => ({
      ...prev,
      [brand]: { ...prev[brand], [field]: value },
    }));
  };

  if (loading)
    return (
      <div className="py-8">
        <Loader className="animate-spin" />
      </div>
    );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold text-xl">Home Feature Section</h2>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-black text-white px-6 py-2 font-heading font-bold uppercase tracking-widest hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2 text-sm"
        >
          {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
          Save Changes
        </button>
      </div>

      {message && (
        <div
          className={`p-3 mb-6 text-sm ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <p className="text-sm text-gray-500 mb-8">
        Configure the feature section on the homepage. It appears below the hero slider.
      </p>

      <div className="grid grid-cols-1 gap-8 max-w-2xl">
        {/* 4HVNSK8 Section */}
        <div className="bg-white border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-3 h-3 bg-black rounded-full" />
            <h3 className="font-heading font-bold text-lg uppercase tracking-widest">4HVNSK8</h3>
          </div>

          <div className="space-y-4">
            {/* Background Image */}
            <ImageUploader
              value={settings['4HVNSK8'].backgroundImage}
              onChange={(url) => updateSetting('4HVNSK8', 'backgroundImage', url)}
              pathPrefix="content/brand-4hvnsk8"
              aspectClass="aspect-video"
              onError={(text) => setMessage({ type: 'error', text })}
              onUploaded={() => setMessage({ type: 'success', text: 'Image uploaded successfully' })}
              previewOverlay={
                settings['4HVNSK8'].backgroundImage ? (
                  <>
                    <div
                      className="absolute inset-0 bg-gradient-to-br from-black to-gray-900"
                      style={{ opacity: settings['4HVNSK8'].overlayOpacity / 100 }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-white">
                        {settings['4HVNSK8'].headline.split('\n').map((line, i) => (
                          <p key={i} className="font-heading text-xl font-bold tracking-widest">{line}</p>
                        ))}
                      </div>
                    </div>
                  </>
                ) : undefined
              }
            />

            {/* Headline */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
                Headline (use Enter for line breaks)
              </label>
              <textarea
                value={settings['4HVNSK8'].headline}
                onChange={(e) => updateSetting('4HVNSK8', 'headline', e.target.value)}
                className="w-full text-sm p-2 border border-gray-200 font-heading uppercase tracking-widest"
                rows={2}
                placeholder="PREMIUM&#10;COLLECTION"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
                Description
              </label>
              <textarea
                value={settings['4HVNSK8'].description}
                onChange={(e) => updateSetting('4HVNSK8', 'description', e.target.value)}
                className="w-full text-sm p-2 border border-gray-200"
                rows={3}
                placeholder="Description text..."
              />
            </div>

            {/* CTA Button */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
                  Button Text
                </label>
                <input
                  type="text"
                  value={settings['4HVNSK8'].ctaText}
                  onChange={(e) => updateSetting('4HVNSK8', 'ctaText', e.target.value)}
                  className="w-full text-sm p-2 border border-gray-200"
                  placeholder="EXPLORE 4HVNSK8"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
                  Button Link
                </label>
                <input
                  type="text"
                  value={settings['4HVNSK8'].ctaLink}
                  onChange={(e) => updateSetting('4HVNSK8', 'ctaLink', e.target.value)}
                  className="w-full text-sm p-2 border border-gray-200 font-mono"
                  placeholder="/4hvnsk8"
                />
              </div>
            </div>

            {/* Overlay Opacity */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">
                Overlay Opacity: {settings['4HVNSK8'].overlayOpacity}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={settings['4HVNSK8'].overlayOpacity}
                onChange={(e) => updateSetting('4HVNSK8', 'overlayOpacity', parseInt(e.target.value))}
                className="w-full"
              />
              <p className="text-xs text-gray-400 mt-1">
                Higher values make the background darker for better text readability
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BrandSectionsManager;
